import type {
  RespiteCarespaceRef,
  RespiteEligibleRecord,
  RespiteFilters,
  RespiteHsaRecord,
  RespiteNeedsAttentionResponse,
  RespiteNeedsAttentionRow,
  RespiteReferralRecord,
  RespiteSummaryResponse,
} from "../../../../../services/control-plane/src/tools/dashboards/respite.types";

export type * from "../../../../../services/control-plane/src/tools/dashboards/respite.types";

export type RespiteParams = {
  dateStart: string;
  dateEnd: string;
  clinic?: string;
  navigator?: string;
  provider?: string;
};

const HSA_BUCKETS = new Set([
  "completed_on_time",
  "completed_late",
  "open_in_window",
  "open_overdue",
  "declined",
  "ineligible",
  "no_answer",
]);
const REFERRAL_STATUSES = new Set(["pending", "in_progress", "completed", "cancelled", "archived"]);

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string");
}

function hasStrings(value: Record<string, unknown>, keys: string[]): boolean {
  return keys.every((key) => typeof value[key] === "string");
}

function hasBooleans(value: Record<string, unknown>, keys: string[]): boolean {
  return keys.every((key) => typeof value[key] === "boolean");
}

const REF_KEYS = ["carespaceId", "clinicName", "navigatorName"];

function isCarespaceRef(value: unknown): value is RespiteCarespaceRef {
  return isObject(value) && hasStrings(value, REF_KEYS);
}

function isFilters(value: unknown): value is RespiteFilters {
  return (
    isObject(value) &&
    isStringArray(value.clinics) &&
    isStringArray(value.navigators) &&
    isStringArray(value.providers)
  );
}

function isHsaRecord(value: unknown): value is RespiteHsaRecord {
  return (
    isObject(value) &&
    hasStrings(value, [...REF_KEYS, "providerName", "providerGroup", "clockStart"]) &&
    HSA_BUCKETS.has(value.bucket as string) &&
    hasBooleans(value, ["requested", "dueSoon"])
  );
}

function isReferralRecord(value: unknown): value is RespiteReferralRecord {
  return (
    isObject(value) &&
    hasStrings(value, [...REF_KEYS, "engagementId", "providerName", "providerGroup"]) &&
    REFERRAL_STATUSES.has(value.status as string) &&
    hasBooleans(value, [
      "rereferred",
      "carespaceInactive",
      "carePlanOnFile",
      "carePlanUpdatedSinceReferral",
    ])
  );
}

function isEligibleRecord(value: unknown): value is RespiteEligibleRecord {
  return isObject(value) && hasStrings(value, [...REF_KEYS, "state", "county"]);
}

function isNeedsAttentionRow(value: unknown): value is RespiteNeedsAttentionRow {
  return (
    isObject(value) &&
    hasStrings(value, [
      "engagementId",
      "carespaceId",
      "carespaceUrl",
      "patientName",
      "clinicName",
      "navigatorName",
      "providerName",
      "resourceType",
      "engagementStatus",
      "referredOn",
      "guideState",
    ]) &&
    (value.serviceKind === "home_visit" || value.serviceKind === "respite") &&
    isFiniteNumber(value.daysOpen) &&
    (value.pylonStatus === null || typeof value.pylonStatus === "string") &&
    hasBooleans(value, ["respiteEligible", "carePlanOnFile", "carePlanUpdatedSinceReferral"])
  );
}

export function isRespiteSummaryResponse(value: unknown): value is RespiteSummaryResponse {
  return (
    isObject(value) &&
    hasStrings(value, ["dateStart", "dateEnd", "timezone", "generatedAt"]) &&
    isFilters(value.filters) &&
    isFilters(value.filterOptions) &&
    isFiniteNumber(value.hsaTargetDays) &&
    isObject(value.toDate) &&
    hasStrings(value.toDate, ["asOf", "deadlineFrom", "deadlineTo", "clockFrom", "clockTo"]) &&
    Array.isArray(value.missingInitialPaf) &&
    value.missingInitialPaf.every(isCarespaceRef) &&
    Array.isArray(value.hsaRecords) &&
    value.hsaRecords.every(isHsaRecord) &&
    Array.isArray(value.referrals) &&
    value.referrals.every(isReferralRecord) &&
    Array.isArray(value.eligible) &&
    value.eligible.every(isEligibleRecord) &&
    (value.source === "live" || value.source === "mock")
  );
}

export function isRespiteNeedsAttentionResponse(
  value: unknown,
): value is RespiteNeedsAttentionResponse {
  return (
    isObject(value) &&
    isFilters(value.filters) &&
    Array.isArray(value.rows) &&
    value.rows.every(isNeedsAttentionRow) &&
    typeof value.generatedAt === "string" &&
    (value.source === "live" || value.source === "mock")
  );
}
